import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, AlertTriangle, FileText, User, Phone, Mail } from 'lucide-react';
import { Driver } from '../../../types/saas';
import saasApiService from '../../../services/saasApi';

interface DriverListProps {
  onEdit: (driver: Driver) => void;
  onAdd: () => void;
  refreshTrigger: number;
}

const DriverList: React.FC<DriverListProps> = ({ onEdit, onAdd, refreshTrigger }) => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  useEffect(() => {
    fetchDrivers();
  }, [refreshTrigger]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const response = await saasApiService.getDrivers();
      // El backend puede devolver diferentes estructuras
      let driverData = [];
      if (response && response.drivers && Array.isArray(response.drivers)) {
        // Estructura directa: { drivers: [...], pagination: {...} }
        driverData = response.drivers;
      } else if (response && response.data && response.data.drivers) {
        // Estructura anidada: { success: true, data: { drivers: [...], pagination: {...} } }
        driverData = response.data.drivers;
      } else if (Array.isArray(response)) {
        driverData = response;
      } else if (response && response.items) {
        driverData = response.items;
      } else if (response && response.data && Array.isArray(response.data)) {
        driverData = response.data;
      }
      setDrivers(driverData);
    } catch (error: any) {
      console.error('Error fetching drivers:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este conductor?')) {
      try {
        await saasApiService.deleteDriver(id);
        fetchDrivers();
      } catch (error) {
        console.error('Error deleting driver:', error);
      }
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.license_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (driver.email && driver.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || driver.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const total = filteredDrivers.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pageDrivers = filteredDrivers.slice(startIdx, endIdx);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'inactive': return 'Inactivo';
      case 'suspended': return 'Suspendido';
      default: return status;
    }
  };

  const getLicenseTypeText = (type: string) => {
    const types = {
      'A': 'Clase A (Motocicletas)',
      'B': 'Clase B (Automóviles)',
      'C': 'Clase C (Camiones)',
      'D': 'Clase D (Transporte público)',
      'E': 'Clase E (Articulados)'
    };
    return types[type as keyof typeof types] || type;
  };

  const isDocumentExpiringSoon = (date: string) => {
    if (!date) return false;
    const expiryDate = new Date(date);
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return expiryDate <= thirtyDaysFromNow;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Conductores</h1>
          <p className="text-gray-600">Administra tu equipo de conductores</p>
        </div>
        <button
          onClick={onAdd}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Conductor
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar por nombre, licencia o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
          <option value="suspended">Suspendido</option>
        </select>
      </div>

      {/* Driver Grid */}
      {filteredDrivers.length === 0 ? (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay conductores</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'No se encontraron conductores con los filtros aplicados.'
              : 'Comienza agregando tu primer conductor.'
            }
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <div className="mt-6">
              <button
                onClick={onAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Conductor
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pageDrivers.map((driver) => (
            <div key={driver.id} className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <User className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {driver.first_name} {driver.last_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {getLicenseTypeText(driver.license_type)}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(driver.status)}`}>
                    {getStatusText(driver.status)}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Licencia:</span>
                    <span className="text-sm font-medium text-gray-900">{driver.license_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Vence:</span>
                    <span className={`text-sm font-medium ${isDocumentExpiringSoon(driver.license_expiry) ? 'text-red-600' : 'text-gray-900'}`}>
                      {new Date(driver.license_expiry).toLocaleDateString()}
                    </span>
                  </div>
                  {driver.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="h-3 w-3 mr-1" />
                      {driver.phone}
                    </div>
                  )}
                  {driver.email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-3 w-3 mr-1" />
                      {driver.email}
                    </div>
                  )}
                  {driver.hire_date && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Contratado:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(driver.hire_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {driver.salary && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Salario:</span>
                      <span className="text-sm font-medium text-gray-900">
                        ${driver.salary.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Document alerts */}
                <div className="mb-4">
                  {isDocumentExpiringSoon(driver.license_expiry) && (
                    <div className="flex items-center text-red-600 text-xs mb-1">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Licencia vence pronto
                    </div>
                  )}
                  {driver.medical_certificate_expiry && isDocumentExpiringSoon(driver.medical_certificate_expiry) && (
                    <div className="flex items-center text-yellow-600 text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Certificado médico vence pronto
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="flex space-x-2">
                    {driver.license_document && (
                      <div title="Documento de licencia">
                        <FileText className="h-4 w-4 text-green-600" />
                      </div>
                    )}
                    {driver.medical_certificate && (
                      <div title="Certificado médico">
                        <FileText className="h-4 w-4 text-blue-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEdit(driver)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Editar conductor"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(driver.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Eliminar conductor"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Pagination controls */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-600">
            Mostrando {total === 0 ? 0 : startIdx + 1} - {Math.min(endIdx, total)} de {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-50"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Anterior
            </button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button
              className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-50"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Siguiente
            </button>
            <select
              className="ml-2 px-2 py-1.5 text-sm border border-gray-300 rounded"
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            >
              <option value={6}>6</option>
              <option value={9}>9</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
            </select>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default DriverList;
